// src/pages/admin/ReportesPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getOrdersNormalized,
  getBanks,
  getSuppliers,
  type OC,
  type Banco,
  type Proveedor,
} from "../../services/api";

const money = (n?: number) =>
  typeof n === "number"
    ? n.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
    : "—";

const inRange = (iso: string | null | undefined, d1: string, d2: string) => {
  if (!iso) return false;
  const x = new Date(iso).getTime();
  const a = new Date(d1 + "T00:00:00").getTime();
  const b = new Date(d2 + "T23:59:59").getTime();
  return x >= a && x <= b;
};

const LIMIT_PCT = 33; 

const ReportesPage: React.FC = () => {
  const [ocs, setOcs] = useState<OC[]>([]);
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);

  // Filtros de fecha (por defecto este mes)
  const date = new Date();
  const primerDia = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
  const hoy = new Date().toISOString().slice(0, 10);
  
  const [desde, setDesde] = useState<string>(primerDia);
  const [hasta, setHasta] = useState<string>(hoy);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carga de datos base
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [ordersRes, banksData, suppliersData] = await Promise.all([
          getOrdersNormalized(),
          getBanks(),
          getSuppliers(),
        ]);
        setOcs(ordersRes);
        setBancos(banksData);
        setProveedores(suppliersData);
      } catch (err: any) {
        setError("No se pudieron cargar los datos para el reporte. Verifica la conexión.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 1. Filtrado por fecha
  const filtered = useMemo(
    () => ocs.filter((o) => inRange(o.created, desde, hasta)),
    [ocs, desde, hasta]
  );

  // 2. Cálculos de agregación
  const stats = useMemo(() => {
    let totalPresupuesto = 0;
    let totalGastado = 0;

    const bankMap = new Map<string | number, number>();
    const suppMap = new Map<string | number, number>();

    const rows = filtered.map((o) => {
      totalPresupuesto += o.presupuestoTotal;
      totalGastado += o.totalEstimado;

      o.expenses.forEach((e) => {
        if (e.bankId) bankMap.set(e.bankId, (bankMap.get(e.bankId) ?? 0) + e.amountSpent);
        if (e.supplierId) suppMap.set(e.supplierId, (suppMap.get(e.supplierId) ?? 0) + e.amountSpent);
      });

      return {
        id: o.id,
        folio: o.folio,
        fecha: o.created,
        presupuesto: o.presupuestoTotal,
        gastado: o.totalEstimado,
        pct: o.presupuestoTotal > 0 ? (o.totalEstimado / o.presupuestoTotal) * 100 : 0,
        cliente: o.customerName
      };
    });

    const byBank = Array.from(bankMap.entries())
      .map(([id, amt]) => ({
        id: String(id),
        name: bancos.find((b) => String(b.id) === String(id))?.nombre || `Banco ${id}`,
        amount: amt,
      }))
      .sort((a, b) => b.amount - a.amount);

    const bySupplier = Array.from(suppMap.entries())
      .map(([id, amt]) => ({
        id: String(id),
        name: proveedores.find((p) => String(p.id) === String(id))?.nombre || `Prov. ${id}`,
        amount: amt,
      }))
      .sort((a, b) => b.amount - a.amount);

    return {
      totalCount: filtered.length,
      totalPresupuesto,
      totalGastado,
      avgPct: filtered.length > 0 ? rows.reduce((s, r) => s + r.pct, 0) / filtered.length : 0,
      byBank,
      bySupplier,
      rows: rows.sort((a, b) => b.presupuesto - a.presupuesto),
    };
  }, [filtered, bancos, proveedores]);

  const exportCSV = () => {
    const headers = ["Folio", "Cliente", "Fecha", "Presupuesto", "Gastado", "% Gasto"];
    const csvRows = stats.rows.map((r) => [
      r.folio,
      r.cliente,
      r.fecha ? new Date(r.fecha).toLocaleDateString() : "—",
      r.presupuesto.toFixed(2),
      r.gastado.toFixed(2),
      r.pct.toFixed(1) + "%",
    ]);
    const csvContent = [headers, ...csvRows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Reporte_OCs_${desde}_${hasta}.csv`;
    link.click();
  };

  return (
    <div className="w-full px-4 sm:px-6 pt-24 pb-10 bg-slate-50 min-h-screen">
      <header className="mx-auto max-w-[1200px] mb-8">
        <div className="rounded-3xl bg-slate-900 p-8 shadow-2xl text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-3xl rounded-full -mr-20 -mt-20" />
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h2 className="text-4xl font-black tracking-tight">Análisis Operativo</h2>
              <p className="text-slate-400 mt-2 font-medium">Control de ejecución presupuestaria</p>
            </div>
            <button 
              onClick={exportCSV} 
              disabled={loading || stats.totalCount === 0}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-all disabled:opacity-50"
            >
              📥 Exportar (.csv)
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <span className="text-[10px] uppercase font-black text-slate-500 tracking-widest">Órdenes</span>
              <p className="text-2xl font-bold">{loading ? "..." : stats.totalCount}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-emerald-400">
              <span className="text-[10px] uppercase font-black text-slate-500 tracking-widest">Presupuestado</span>
              <p className="text-2xl font-bold">{loading ? "..." : money(stats.totalPresupuesto)}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-rose-400">
              <span className="text-[10px] uppercase font-black text-slate-500 tracking-widest">Gastado Real</span>
              <p className="text-2xl font-bold">{loading ? "..." : money(stats.totalGastado)}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <span className="text-[10px] uppercase font-black text-slate-500 tracking-widest">Ejecución Prom.</span>
              <p className="text-2xl font-bold">{loading ? "..." : stats.avgPct.toFixed(1) + "%"}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-8 pt-8 border-t border-white/10">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase opacity-50">Desde</label>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="bg-slate-800 border-none rounded-lg text-sm p-2 text-white" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase opacity-50">Hasta</label>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="bg-slate-800 border-none rounded-lg text-sm p-2 text-white" />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] space-y-8">
        {error && (
          <div className="p-4 rounded-xl bg-red-100 border border-red-200 text-red-700 font-bold">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="h-64 bg-slate-200 animate-pulse rounded-3xl" />
            <div className="h-64 bg-slate-200 animate-pulse rounded-3xl" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">🏦 Gasto por Banco</h3>
                <div className="space-y-4">
                  {stats.byBank.length > 0 ? stats.byBank.map(b => (
                    <div key={b.id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-slate-600">{b.name}</span>
                        <span className="font-bold text-slate-900">{money(b.amount)}</span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500" style={{ width: `${(b.amount / (stats.totalGastado || 1)) * 100}%` }} />
                      </div>
                    </div>
                  )) : <p className="text-slate-400 italic">No hay datos de bancos</p>}
                </div>
              </section>

              <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">📦 Gasto por Proveedor</h3>
                <div className="space-y-4">
                  {stats.bySupplier.length > 0 ? stats.bySupplier.map(s => (
                    <div key={s.id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-slate-600">{s.name}</span>
                        <span className="font-bold text-slate-900">{money(s.amount)}</span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${(s.amount / (stats.totalGastado || 1)) * 100}%` }} />
                      </div>
                    </div>
                  )) : <p className="text-slate-400 italic">No hay datos de proveedores</p>}
                </div>
              </section>
            </div>

            <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                <h3 className="font-bold text-slate-800 text-sm">Ejecución por Orden de Compra</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-[10px] font-black uppercase text-slate-400 bg-slate-50">
                    <tr>
                      <th className="px-6 py-4">Folio / Cliente</th>
                      <th className="px-6 py-4">Fecha</th>
                      <th className="px-6 py-4 text-right">Presupuesto</th>
                      <th className="px-6 py-4 text-right">Gasto Real</th>
                      <th className="px-6 py-4 text-center">% Gasto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {stats.rows.length > 0 ? stats.rows.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-900">{r.folio}</div>
                          <div className="text-[11px] text-slate-500">{r.cliente}</div>
                        </td>
                        <td className="px-6 py-4 text-slate-500">
                          {r.fecha ? new Date(r.fecha).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-slate-600">{money(r.presupuesto)}</td>
                        <td className="px-6 py-4 text-right font-black text-slate-900">{money(r.gastado)}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.pct > 100 ? 'bg-rose-100 text-rose-700' : r.pct > LIMIT_PCT ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {r.pct.toFixed(1)}%
                            </span>
                            <div className="h-1 w-16 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full ${r.pct > 100 ? 'bg-rose-500' : 'bg-indigo-500'}`} style={{ width: `${Math.min(r.pct, 100)}%` }} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )) : <tr><td colSpan={5} className="py-10 text-center text-slate-400 italic">No hay datos en el rango seleccionado</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default ReportesPage;